/* eslint-disable no-unused-vars */
import { useEffect, useState } from 'react';
import Table from '../Table/Table';
import moment from 'moment';
import {
  ChevronDoubleDownIcon,
  PlusCircleIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import queryString from 'query-string';
import classNames from 'classnames';
import ReactPaginate from 'react-paginate';
import { useLocation, useNavigate } from 'react-router-dom';
import { controllers, requestTypes } from '../../constants/enums/API';
import request from '../../utils/APIHelper/request';
import loadingGif from '../../assets/Other/loading.gif';
import {
  convertStringToInt,
  convertStringToNumber,
} from '../../utils/ConvertStringToNumber/convertStringToNumber';
import { isNullOrEmpty } from '../../utils/StringUtils/StringUtils';
import ParcelRatesFilters from './Filters/ParcelRatesFilter';

const doubleRow = (header1, value1, header2, value2) => {
  return (
    <div>
      <div className="font-bold">{header1}</div>
      <div>{value1}</div>
      <div className="font-bold">{header2}</div>
      <div>{value2}</div>
    </div>
  );
};

const dateTimeRow = (value) => {
  if (!value) return '---';
  return (
    <div>
      <div>{moment(value).format('MMM DD, YYYY')}</div>
      <div>{moment(value).format('hh:mmA')}</div>
    </div>
  );
};

const transformData = (data) => {
  return data?.map((item) => ({
    id: item.id,
    branchId: item.branchId,
    agent: item.agent,
    country: item.country,
    rateName: item.rateName,
    weightCode: item.weightCode,
    weight: doubleRow('From:', item.weightFrom, 'To:', item.weightTo),

    status: item.status,
    statusFrom: doubleRow(
      'From:',
      moment(item.statusFrom).format('MMM DD, YYYY'),
      'To:',
      moment(item.statusTo).format('MMM DD, YYYY'),
    ),
    sae: item.sae,
    deliveryModeCode: item.deliveryModeCode,
    perKg: doubleRow('Meest:', item.perKgM, 'Agent:', item.perKgA),
    perEach: doubleRow('Meest:', item.perEachM, 'Agent:', item.perEachA),
    delivery: doubleRow('Meest:', item.deliveryM, 'Agent:', item.deliveryA),
    insurance: doubleRow('Meest:', item.insuranceM, 'Agent:', item.insuranceA),
    packaging: doubleRow(
      'Meest:',
      item.packagingM || '---',
      'Agent:',
      item.packagingA || '---',
    ),
    firstDateTime: dateTimeRow(item.firstDateTime),
    editDateTime: dateTimeRow(item.editDateTime),
    source: item.source,
  }));
};

const ParcelRatesTable = () => {
  const { search } = useLocation();
  const params = queryString.parse(search);

  const defaultRateFilter = {
    id: params.id || null,
    branchId: params.branchId || null,
    agent: params.agent || null,
    country: params.country || null,
    rateName: params.rateName || null,
    weightCode: params.weightCode || null,
    weightFrom: params.weightFrom || null,
    weightTo: params.weightTo || null,
    status: params.status || null,
    statusFrom: params.statusFrom || null,
    statusTo: params.statusTo || null,
    sae: params.sae || null,
    deliveryModeCode: params.deliveryModeCode || null,
    perKgA: params.perKgA || null,
    perKgW: params.perKgW || null,
    perKgM: params.perKgM || null,
    perEachA: params.perEachA || null,
    perEachW: params.perEachW || null,
    perEachM: params.perEachM || null,
    deliveryM: params.deliveryM || null,
    deliveryA: params.deliveryA || null,
    deliveryW: params.deliveryW || null,
    insuranceM: params.insuranceM || null,
    insuranceW: params.insuranceW || null,
    insuranceA: params.insuranceA || null,
    packagingM: params.packagingM || null,
    packagingW: params.packagingW || null,
    packagingA: params.packagingA || null,
    firstDateTime: params.firstDateTime || null,
    editDateTime: params.editDateTime || null,
    source: params.source || null,
  };

  const [filter, setFilter] = useState(defaultRateFilter);
  const [pageLoading, setPageLoading] = useState(false);

  const navigate = useNavigate();
  const [parcelRates, setParcelRates] = useState();
  const [totalItems, setTotalItems] = useState();
  const [totalPages, setTotalPages] = useState();
  const [recentlyUpdatedData, setrecentlyUpdatedData] = useState([]);
  const [data, setData] = useState([]);
  const openParcelRatesCreatingModal = () => {};

  const setFilterValue = (field, currentValue, type = 'string') => {
    let value = '';
    switch (type) {
      case 'number':
        value = convertStringToNumber(currentValue, true);
        break;
      case 'number-int':
        value = convertStringToInt(currentValue, true);
        break;
      default:
        value = currentValue;
    }

    if (value === '' || (type === 'number' && isNaN(value))) {
      value = null;
    }

    setFilter((prevFilter) => ({
      ...prevFilter,
      [field]: value,
    }));
  };

  const headerWitchIcons = (headerName) => {
    return (
      <div className="relative text-xs">
        <div>{headerName}</div>
        <div>
          <ChevronDoubleDownIcon className="w-5 h-5 mx-auto text-blue-900" />
        </div>
      </div>
    );
  };

  const headers = [
    headerWitchIcons('Id'),
    headerWitchIcons('Branch'),
    headerWitchIcons('Agent'),
    headerWitchIcons('Country'),
    headerWitchIcons('Rate Name'),
    headerWitchIcons('Unit'),
    headerWitchIcons('Weight'),
    headerWitchIcons('Status'),
    headerWitchIcons('Active'),
    headerWitchIcons('Sea/Air'),
    headerWitchIcons('Delivery Mode'),
    // { element: 'delivery Mode', columnClassName: 'p-0' },
    headerWitchIcons('per kg/lb'),
    headerWitchIcons('Per Each'),
    headerWitchIcons('Delivery'),
    headerWitchIcons('Insurance'),
    headerWitchIcons('Packaging'),
    headerWitchIcons('Reg-tion Date'),
    headerWitchIcons('Edit Date'),
    headerWitchIcons('Source'),
  ];

  const getQueryParamsByPage = (pageNum) => {
    const cleanedFilter = Object.fromEntries(
      Object.entries(filter).filter(
        ([_, value]) => value != null && value !== '',
      ),
    );
    return {
      ...cleanedFilter,
      page: pageNum,
    };
  };

  useEffect(() => {
    const fetchData = async () => {
      const result = await getParcelRates(params.page);
      setParcelRates(result?.parcelRates);
      setTotalItems(result?.totalItems);
      setTotalPages(result?.totalPages);
      setData(result?.parcelRates);
    };
    fetchData();
  }, [search]);

  const getParcelRates = async (page = 1) => {
    const result = await request({
      url: `${controllers.parcelRates}getParcelRatesByParams`,
      type: requestTypes.post,
      params: { page },
      body: filter,
      disableLogOut: true,
      setLoading: setPageLoading,
    });
    return result;
  };

  const applyFilters = () => {
    const settedFilterFields = Object.fromEntries(
      Object.entries(filter).filter(
        ([, value]) => value !== null && value !== undefined,
      ),
    );

    // Convert to query string
    const query = queryString.stringify(settedFilterFields);

    // Redirect to the page
    navigate(`/parcelRates?${query}`);
  };

  const filters = () => {
    return (
      <>
        <div className="mx-5">Filters:</div>
        <div className="flex flex-wrap items-center">
          <ParcelRatesFilters setFilterValue={setFilterValue} filter={filter} />
        </div>
        <div className="text-center">
          <div
            onClick={applyFilters}
            className="inline-block bg-blue-900 hover:bg-blue-800 text-white px-3 py-1 rounded-full cursor-pointer hover:transition-all hover:duration-30"
          >
            Apply Filters
          </div>
        </div>
      </>
    );
  };

  return (
    <div>
      {pageLoading ? (
        <>
          <div className="flex items-center justify-center">
            <img src={loadingGif} className="w-6 h-6" />
          </div>
        </>
      ) : (
        <>
          {filters()}
          {/* <div className="my-9 mx-4">
            Add new rates
            <div
              onClick={openParcelRatesCreatingModal}
              data-tooltip-id="agent-adding-tooltip"
              className="cursor-pointer bg-blue-900 text-white rounded-full h-6 w-6 inline-flex items-center justify-center pb-[2px] mx-2 hover:bg-blue-800 hover:transition-all hover:duration-300"
            >
              +
            </div>
          </div> */}
          <div className="m-5">Total: {totalItems}</div>
          <div className="mt-4 mb-9">
            <ReactPaginate
              onPageChange={(e) => {
                const redirectPage = Number.parseInt(e.selected) + 1;
                navigate(
                  `/parcelRates?${queryString.stringify(getQueryParamsByPage(redirectPage))}`,
                );
              }}
              forcePage={Number.parseInt(params.page) - 1 || 0}
              pageCount={totalPages}
              pageRangeDisplayed={4}
              marginPagesDisplayed={2}
              containerClassName={classNames(
                'flex flex-row w-1/2 float-left my-0 text-sm xl:text-sm',
              )}
              activeLinkClassName="!text-blue-400 !border-blue-400"
              pageLinkClassName="mx-3 text-blue-700 border-[1px] border-blue-700 rounded-xl min-w-[20px] px-[5px] xl:px-2 py-[1px] xl:py-[1px] hover:text-blue-400 hover:border-blue-400 hover:duration-300"
              previousLinkClassName="mx-3 text-blue-700 border-[1px] border-blue-700 rounded-xl min-w-[20px] px-[5px] xl:px-2 py-[1px] xl:py-[1px] hover:text-blue-400 hover:border-blue-400 hover:duration-300"
              nextLinkClassName="mx-3 text-blue-700 border-[1px] border-blue-700 rounded-xl min-w-[20px] px-[5px] xl:px-2 py-[1px] xl:py-[1px] hover:text-blue-400 hover:border-blue-400 hover:duration-300"
            />
          </div>
          <br />
          <div className="relative">
            <div className="w-full mb-10 relative">
              <Table
                stickyHeaders={true}
                rowsClassName="!text-center border-b border-blue-300"
                headersClassNames="!text-center !bg-white"
                containerClassName="text-[0.9rem] whitespace-nowrap text-center"
                data={transformData(data)}
                headers={headers}
              />
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ParcelRatesTable;
